import { useState, useCallback, useEffect } from 'react'
import { getBulletin } from '@trinity/utils'
import { Button, IconButton } from '../Buttons'
import { Dialog, Typography } from '../Display'
import { KeyboardArrowLeft, KeyboardArrowRight } from '../Icons'
import { Box, Stack } from '../Layout'
import { Spinner } from '../Loading'

export function Bulletins({ bulletins }: { bulletins: TrinityAPI.BulletinType[] }) {
  const [bulletinStep, setBulletinStep] = useState(0)
  const [fullBulletin, setFullBulletin] = useState<TrinityAPI.BulletinExtendedType | null>(null)
  const [loading, setLoading] = useState(false)
  const [openBulletin, setOpenBulletin] = useState(false)
  const incrementStep = useCallback(() => setBulletinStep(bulletinStep => bulletinStep + 1), [])
  const decrementStep = useCallback(() => setBulletinStep(bulletinStep => bulletinStep - 1), [])
  const bulletin = bulletins[bulletinStep]
  const minStep = bulletinStep > 0
  const maxStep = bulletinStep < bulletins.length - 1

  useEffect(() => {
    async function fetchBulletin() {
      if (bulletin && openBulletin) {
        setFullBulletin(null)
        setLoading(true)
        try {
          const fullBulletin = await getBulletin(bulletin.id)
          setFullBulletin(fullBulletin)
        } catch (e) {
          setFullBulletin(null)
        } finally {
          setLoading(false)
        }
      }
    }

    fetchBulletin()
  }, [bulletin, openBulletin])

  return (
    <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ width: 700 }}>
      <IconButton size='small' onClick={decrementStep}>
        {minStep && <KeyboardArrowLeft fontSize='large' />}
      </IconButton>
      <Box sx={{ px: 1, width: '80%' }}>
        <Typography gutterBottom textAlign='center' component='h6' variant='link' onClick={() => setOpenBulletin(true)}>
          {bulletin?.title}
        </Typography>
        <Typography noWrap textAlign='center' component='p' variant='smallBody1'>
          {bulletin?.detail}
        </Typography>
      </Box>
      <IconButton size='small' onClick={incrementStep}>
        {maxStep && <KeyboardArrowRight fontSize='large' />}
      </IconButton>
      <Dialog
        open={openBulletin}
        onClose={() => setOpenBulletin(false)}
        slotProps={{
          paper: { sx: { maxWidth: 'fit-content', alignItems: 'flex-end' } },
        }}
      >
        {loading ? (
          <Stack p={2} width={660} height={400} justifyContent='center' alignItems='center'>
            <Spinner size={200} />
          </Stack>
        ) : fullBulletin ? (
          <div dangerouslySetInnerHTML={{ __html: fullBulletin.description }} />
        ) : (
          <Stack p={2} width={660} height={250} alignItems='center'>
            <h2>We're sorry, something went wrong.</h2>
            <p>Please refresh the page and try again.</p>
            <Button variant='outlined' onClick={() => window.location.reload()}>
              Refresh
            </Button>
          </Stack>
        )}
      </Dialog>
    </Stack>
  )
}
